import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet';
import { Doughnut } from 'react-chartjs-2';
import axios from "axios";
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { buildApiUrl } from "../utils/api";

ChartJS.register(Title, Tooltip, Legend, ArcElement);

const MetricCard = ({ title, value, to }) => {
    const inner = (
        <div className="metric-summary-card shadow" style={{ height: "160px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", borderRadius: "12px", background: "#1a2d45", padding: "1rem", cursor: to ? "pointer" : "default" }}>
            <h4 style={{ color: "#FFD817", fontWeight: "bold", fontSize: "0.85rem", textAlign: "center", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.5rem" }}>{title}</h4>
            <h1 style={{ color: "#fff", fontSize: "2.5rem", fontWeight: "800", margin: 0 }}>{value ?? "—"}</h1>
        </div>
    );
    if (to) return <Link to={to} style={{ textDecoration: "none" }}>{inner}</Link>;
    return inner;
};

function AdminDashboard() {
    const [totalProviders, setTotalProviders] = useState(null);
    const [totalFinders, setTotalFinders] = useState(null);
    const [totalBlogCount, setTotalBlogCount] = useState(null);
    const [totalWarehouses, setTotalWarehouses] = useState(null);
    const [totalSubscribers, setTotalSubscribers] = useState(null);
    const [totalEnquiries, setTotalEnquiries] = useState(null);
    const [totalQuotes, setTotalQuotes] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [providerRes, finderRes, blogRes, warehouseRes, subscriberRes, enquiryRes, quotesRes] = await Promise.allSettled([
                    axios.get(buildApiUrl("/api/getProviders")),
                    axios.get(buildApiUrl("/api/getFinders")),
                    axios.get(buildApiUrl("/api/countBlogs/totals")),
                    axios.get(buildApiUrl("/api/warehouses/totals")),
                    axios.get(buildApiUrl("/api/subscriber/totals")),
                    axios.get(buildApiUrl("/api/enquiries/totalEnquiries")),
                    axios.get(buildApiUrl("/api/warehouseQuote/getAllBookings"))
                ]);

                if (providerRes.status === 'fulfilled') setTotalProviders(providerRes.value.data.length);
                if (finderRes.status === 'fulfilled') setTotalFinders(finderRes.value.data.length);
                if (blogRes.status === 'fulfilled') setTotalBlogCount(blogRes.value.data.count);
                if (warehouseRes.status === 'fulfilled') setTotalWarehouses(warehouseRes.value.data.count);
                if (subscriberRes.status === 'fulfilled') setTotalSubscribers(subscriberRes.value.data.count);
                if (enquiryRes.status === 'fulfilled') setTotalEnquiries(enquiryRes.value.data.count);
                if (quotesRes.status === 'fulfilled') setTotalQuotes(Array.isArray(quotesRes.value.data) ? quotesRes.value.data.length : 0);
            } catch (error) {
                console.error('Error fetching dashboard data', error);
            }
        };
        fetchData();
    }, []);

    const doughnutData = (label, value, color) => ({
        labels: [label, 'Remaining'],
        datasets: [{
            label,
            data: [value || 0, Math.max(0, 100 - (value || 0))],
            backgroundColor: [color, '#2e4a6b'],
            borderWidth: 0,
        }],
    });

    const doughnutOptions = {
        responsive: true,
        cutout: '70%',
        plugins: {
            legend: { position: 'bottom', labels: { color: '#cdd9e8' } },
            tooltip: {
                callbacks: { label: (item) => `${item.dataset.label}: ${item.raw}` },
            },
        },
    };

    return (
        <>
            <Helmet>
                <title>Admin Dashboard | 3PL Search</title>
            </Helmet>
            <div className="adminChart" style={{ padding: "1.5rem" }}>
                <div className="adminHead">
                    <h1 style={{ color: "#fff", marginBottom: "1.5rem" }}>ADMIN DASHBOARD</h1>

                    {/* Row 1 — Charts */}
                    <div className="d-flex flex-wrap gap-4 mb-4">
                        <div className="provider shadow d-flex flex-column align-items-center justify-content-center"
                            style={{ height: "220px", width: "300px", background: "#1a2d45", borderRadius: "12px", padding: "1rem" }}>
                            <h4 style={{ color: "#FFD817", fontWeight: "bold", fontSize: "0.8rem", textTransform: "uppercase" }}>3PL PROVIDERS</h4>
                            <div style={{ width: '160px', height: '160px' }}>
                                {totalProviders !== null
                                    ? <Doughnut data={doughnutData('Providers', totalProviders, '#EA2323')} options={doughnutOptions} />
                                    : <p style={{ color: "#cdd9e8" }}>Loading...</p>}
                            </div>
                        </div>

                        <div className="provider shadow d-flex flex-column align-items-center justify-content-center"
                            style={{ height: "220px", width: "300px", background: "#1a2d45", borderRadius: "12px", padding: "1rem" }}>
                            <h4 style={{ color: "#FFD817", fontWeight: "bold", fontSize: "0.8rem", textTransform: "uppercase" }}>BUYER BRIEFS</h4>
                            <div style={{ width: '160px', height: '160px' }}>
                                {totalFinders !== null
                                    ? <Doughnut data={doughnutData('Buyer Briefs', totalFinders, '#4158BD')} options={doughnutOptions} />
                                    : <p style={{ color: "#cdd9e8" }}>Loading...</p>}
                            </div>
                        </div>
                    </div>

                    {/* Row 2 — Metric Cards */}
                    <div className="d-flex flex-wrap gap-4 mb-4">
                        <div style={{ width: "200px" }}>
                            <MetricCard title="Blog Posts" value={totalBlogCount} />
                        </div>
                        <div style={{ width: "200px" }}>
                            <MetricCard title="Warehouses" value={totalWarehouses} to="/adminDashboard/warehouseList" />
                        </div>
                        <div style={{ width: "200px" }}>
                            <MetricCard title="Subscribers" value={totalSubscribers} />
                        </div>
                        <div style={{ width: "200px" }}>
                            <MetricCard title="Enquiries" value={totalEnquiries} to="/adminDashboard/commonEnquiryList" />
                        </div>
                        <div style={{ width: "200px" }}>
                            <MetricCard title="Quote Requests" value={totalQuotes} to="/adminDashboard/userRequestList" />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AdminDashboard;
